import { useTranslation } from "react-i18next";
import { PolicySubject } from "../../../redux/apiSpecifications/apiClaims";
import { useMemo } from "react";
import { MaterialReactTable, MRT_ColumnDef } from "material-react-table";
import { CellWrapper, IconButtonCellWrapper } from "../../../style/CellWrapper";
import { TableWrapper } from "../../../style/TableWrapper";
import { IconButtonWithConfirm } from "../../Inputs/IconButtons";
import { IconPracticesRejected } from "../../../config/icons";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { MRT_Localization_IT } from "material-react-table/locales/it";
import { MRT_Localization_EN } from "material-react-table/locales/en";

interface IPolicySubjectsListProps {
  data: PolicySubject[];
  onSelectItem?: (item: PolicySubjectsListItem) => void;
  onDeleteItem?: (item: PolicySubjectsListItem) => void;
}

const PolicySubjectsList = ({
  data,
  onSelectItem,
  onDeleteItem,
}: IPolicySubjectsListProps) => {
  const { t } = useTranslation();

  const { language } = useSelector((state: RootState) => state.user) || {};

  const items = useMemo(
    () =>
      data.map((item) => {
        return {
          ...item,
          roleDescription: t(`policy-subject-role-${item.roleCode}`)!,
        } as PolicySubjectsListItem;
      }) || [],
    [data]
  );

  const columns = useMemo<MRT_ColumnDef<PolicySubjectsListItem>[]>(() => {
    const columns: MRT_ColumnDef<PolicySubjectsListItem>[] = [
      {
        accessorKey: "roleDescription",
        header: t("policy-subjects-list-role-description"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.roleDescription}</CellWrapper>
        ),
      },
      {
        accessorKey: "subjectName",
        header: t("policy-subjects-list-subject-name"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.subjectName}</CellWrapper>
        ),
      },
      {
        accessorKey: "subjectTaxId",
        header: t("policy-subjects-list-subject-tax-id"),
        size: 100,
        Cell: ({ row }) => (
          <CellWrapper>{row.original.subjectTaxId}</CellWrapper>
        ),
      },
    ];

    if (onDeleteItem) {
      columns.push({
        header: "Actions",
        Header: <span></span>,
        size: 50,
        enableColumnOrdering: false,
        Cell: ({ row }) => (
          <IconButtonCellWrapper>
            <IconButtonWithConfirm
              onClick={(event) => {
                onDeleteItem(row.original);
                event?.stopPropagation();
              }}
              popconfirmOption={{
                title: t("MESSAGES.confirm-delete-policy-subject"),
                placement: "leftTop",
              }}
            >
              <IconPracticesRejected />
            </IconButtonWithConfirm>
          </IconButtonCellWrapper>
        ),
      });
    }

    return columns;
  }, [data, language]);

  const handleSelectItem = (item: PolicySubjectsListItem) => {
    if (onSelectItem) onSelectItem(item);
  };

  return (
    <TableWrapper>
      <MaterialReactTable
        columns={columns}
        data={items}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableHiding={false}
        enableFilters={true}
        enableColumnActions={false}
        enableColumnDragging={false}
        enableSorting={true}
        enableTopToolbar={true}
        localization={
          language === "it" ? MRT_Localization_IT : MRT_Localization_EN
        }
        muiTableBodyRowProps={({ row }) => ({
          onClick: (event) => {
            handleSelectItem(row.original);
          },
          sx: {
            cursor: onSelectItem !== undefined ? "pointer" : "default",
          },
        })}
      />
    </TableWrapper>
  );
};

export default PolicySubjectsList;

export type PolicySubjectsListItem = PolicySubject & {
  roleDescription?: string;
};
